import React, { useState, useMemo } from "react";
import { Box, Button, Tooltip, Typography, alpha } from "@mui/material";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";
import SectionBox from "../utils/SectionBox";
import SectionTabber from "../utils/SectionTabber";
import CurrentYearIncomeTable from "./CurrentYearIncome";
import TrackerForm from "./TrackerForm";
import Summary from "./Summary";
import TrackerInfo from "./TrackerInfo";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { getCommaOut } from "../../../utils/hooks/dataHandler";

const ForeignIncomeTrackerTabs = ({
  currentYearData,
  previousYearData,
  calculatedTrackingData,
  currentYearTotals,
  previousYearTotals,
  ftcClaims,
  onFtcClaimChange,
  onCurrentYearIncomeChange,
  onPreviousYearIncomeChange,
  onTrackingScheduleChange,
  onLockRow,
  onUnlockRow,
  onLockPreviousYearRow,
  onUnlockPreviousYearRow,
  onDeletePreviousYearRow,
  onAddPreviousYearRow,
  onSortPreviousYearRows,
  previousYearSortDirection,
  onSortTrackingRows,
  trackingSortDirection,
  canEdit = true,
}) => {
  const [activeTab, setActiveTab] = useState("currentYear");
  const [trackerInfoOpen, setTrackerInfoOpen] = useState(false);
  const handleError = useErrorDialog();

  const unlockedCount = currentYearData?.filter((row) => !row.isLocked).length;
  const lockedCount = currentYearData?.filter((row) => row.isLocked).length;
  const previousYearUnlockedCount = previousYearData?.filter(
    (row) => !row.isLocked
  ).length;
  const trackingCount = calculatedTrackingData?.length || 0;

  const currentYearSection = (
    <Box>
      <SectionBox
        title='Current Year Unremitted Foreign Income'
        unsavedCount={unlockedCount}
        totalCounts={currentYearData?.length}
        showUnsavedCount={currentYearData?.length > 0}
        savedTerm='locked'
      >
        <CurrentYearIncomeTable
          data={currentYearData}
          onLockRow={onLockRow}
          onUnlockRow={onUnlockRow}
          onChange={(value, { rowId, field, fieldType }) => {
            if (fieldType === "numeric") {
              value = Number(getCommaOut(value));
            }
            onCurrentYearIncomeChange(value, { rowId, field, fieldType });
          }}
          totals={currentYearTotals}
          canEdit={canEdit}
        />
      </SectionBox>

      <Box sx={{ mt: 3 }}>
        <SectionBox
          title='Prior Year Unremitted Foreign Income'
          unsavedCount={previousYearUnlockedCount}
          totalCounts={previousYearData?.length}
          showUnsavedCount={previousYearData?.length > 0}
          savedTerm='locked'
          onAdd={onAddPreviousYearRow}
          canEdit={canEdit}
        >
          <CurrentYearIncomeTable
            isPriorYear={true}
            data={previousYearData}
            onLockRow={onLockPreviousYearRow}
            onUnlockRow={onUnlockPreviousYearRow}
            onChange={(value, { rowId, field, fieldType }) => {
              if (fieldType === "numeric") {
                value = Number(getCommaOut(value));
              }
              onPreviousYearIncomeChange(value, { rowId, field, fieldType });
            }}
            onRemove={onDeletePreviousYearRow}
            totals={previousYearTotals}
            onSortYaEarned={onSortPreviousYearRows}
            sortDirection={previousYearSortDirection}
            canEdit={canEdit}
          />
        </SectionBox>
      </Box>
    </Box>
  );

  const trackerSectionContent =
    trackingCount === 0 ? (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          color: "text.secondary",
        }}
      >
        <Typography variant='body1' gutterBottom>
          No tracking schedule data yet.
        </Typography>
        <Typography variant='body2'>
          Lock rows from Step 1 (Current Year Income) to populate this table, or
          add prior year rows manually.
        </Typography>
      </Box>
    ) : (
      <TrackerForm
        data={calculatedTrackingData}
        onChange={onTrackingScheduleChange}
        calculatedTrackingData={calculatedTrackingData}
        ftcClaims={ftcClaims}
        onFtcClaimChange={onFtcClaimChange}
        onSortYaEarned={onSortTrackingRows}
        sortDirection={trackingSortDirection}
        canEdit={canEdit}
      />
    );

  const trackerSection = (
    <SectionBox
      title='Unremitted Income Tracker'
      headerActions={
        <Tooltip title='Understand the table columns and calculations' arrow>
          <Button
            className='no-export'
            size='small'
            variant='outlined'
            startIcon={<TableViewOutlinedIcon />}
            onClick={(event) => {
              event.stopPropagation();
              setTrackerInfoOpen(true);
            }}
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              borderRadius: 1.5,
              minHeight: 32,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                borderColor: "primary.main",
              },
            }}
          >
            Table guide
          </Button>
        </Tooltip>
      }
    >
      {trackerSectionContent}
    </SectionBox>
  );

  const tabs = [
    {
      label: "Foreign Income",
      value: "currentYear",
      description: "Add and manage foreign income entries.",
      icon: <LooksOneIcon color='primary' fontSize='medium' />,
      badgeCount: unlockedCount,
      badgeColor: "warning",
      content: currentYearSection,
    },
    {
      label: "Unremitted Income Tracker",
      value: "tracker",
      description: "Track and reconcile unremitted balances.",
      icon: <LooksTwoIcon color='primary' fontSize='medium' />,
      chipColor: "info",
      content: trackerSection,
    },
  ];

  return (
    <>
      <SectionTabber
        tabs={tabs}
        value={activeTab}
        onTabChange={(_, newValue) => setActiveTab(newValue)}
      />
      <TrackerInfo
        open={trackerInfoOpen}
        onClose={() => setTrackerInfoOpen(false)}
      />
    </>
  );
};

export default ForeignIncomeTrackerTabs;
